import { AxiosError } from 'axios'
import { action, computed, flow, makeObservable, observable } from 'mobx'

import { requestWrapper } from 'Shared/utils'
import { openPdfDocumentFromBlob } from 'Shared/utils'

import { ALL, STORE_UI } from 'Core/constants'
import { CoreUrlParams, CustomGenerator, SortingParams } from 'Core/interfaces'
import { TableStore } from 'Core/stores/tableStore/TableStore'
import { emptyCustomLabelValue, entityToLabelWithDashValue } from 'Core/utils'
import { getDefaultStoreArrayValue, storagePageSizeConstant } from 'Core/utils/storeHelpers'

import { UrlParams } from 'Portals/Customer/constants'
import { CustomerInvoice, State } from 'Portals/Customer/entities'
import {
	filtersConfig,
	invoiceIdSearchQuery,
	mainSearchConfig,
	searchByCityConfig
} from 'Portals/Customer/Pages/CustomerInvoice/TableList/Filters/utils/config'
import { CustomerInvoiceRepo, ReportRepo, ServiceSiteRepo } from 'Portals/Customer/repositories'
import { RootStore } from 'Portals/Customer/stores'
import { stateToLabelValue } from 'Portals/Customer/utils'

const STORAGE_CUSTOMER_INVOICE_PAGE_SIZE = storagePageSizeConstant('customerInvoice')

export class CustomerInvoiceTable extends TableStore<CustomerInvoice.Basic> {
	@observable public accessor serviceSites = getDefaultStoreArrayValue<Entity>()
	@observable public accessor states = getDefaultStoreArrayValue<State>()

	constructor(private rootStore: RootStore) {
		super(STORAGE_CUSTOMER_INVOICE_PAGE_SIZE)
	}

	@computed
	get serviceSiteOptions(): LabelValue[] {
		return [
			emptyCustomLabelValue(ALL),
			...this.serviceSites.value.map(entityToLabelWithDashValue)
		]
	}

	@computed
	get statesOptions(): LabelValue[] {
		return [emptyCustomLabelValue(ALL), ...this.states.value.map(stateToLabelValue)]
	}

	@flow.bound
	*getItems(defaultSort?: SortingParams) {
		try {
			yield this.requestData({
				repository: CustomerInvoiceRepo.getList,
				defaultSort
			})
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@flow.bound
	*downloadInvoiceDoc(
		documentName: string,
		reportId: number,
		invoiceId: number
	): CustomGenerator<Blob> {
		try {
			const document = yield ReportRepo.generatePdfReport(reportId, invoiceId)
			openPdfDocumentFromBlob(document, documentName)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@action.bound
	initFromURLParams = (params: Record<string, string[]>) => {
		const searchFilterKeys = [UrlParams.id, UrlParams.cityName, CoreUrlParams.searchQuery]

		const filterKeys = [
			UrlParams.includeUnpaid,
			UrlParams.cityName,
			UrlParams.startDate,
			UrlParams.endDate,
			UrlParams.serviceSiteId,
			UrlParams.stateId
		]

		const mergedSearchConfig = {
			...mainSearchConfig,
			invoiceIdSearchQuery,
			searchByCityConfig
		}

		this.initSearchParamsFromUrl(params, searchFilterKeys, mergedSearchConfig)
		this.initFilterParamsFromUrl(params, filterKeys, filtersConfig)
	}

	retrieveFiltersData = () => {
		this.retrieveServiceSites()
		this.retrieveStates()
	};

	@flow.bound
	*retrieveServiceSites() {
		yield requestWrapper({
			field: this.serviceSites,
			repository: ServiceSiteRepo.retrieveServiceSites,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveStates() {
		yield requestWrapper({
			field: this.states,
			repository: CustomerInvoiceRepo.retrieveStates,
			repositoryParams: null
		})
	}

	clearFilterData = () => {
		this.serviceSites = getDefaultStoreArrayValue<Entity>()
		this.states = getDefaultStoreArrayValue<State>()
	}
}
